import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { useCallback, useEffect, useState } from "react";
import { useAuth } from "../context/AuthContext";
import { useInstallPWA } from "../lib/useInstallPWA";
import useTenantPath from "../lib/useTenantPath";
import api from "../api";
import { LogOut, ListOrdered, Users, History, Settings, User as UserIcon, Download, Share2, ShieldCheck } from "lucide-react";
import SupportBanner from "./SupportBanner";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "./ui/dialog";

export default function Layout() {
  const { user, logout, tenant: authTenant } = useAuth();
  const nav = useNavigate();
  const t = useTenantPath();
  const { canInstall, canPrompt, isIos, prompt } = useInstallPWA();
  const [showIosHelp, setShowIosHelp] = useState(false);
  const [tenant, setTenant] = useState(authTenant || null);

  const loadTenant = useCallback(async () => {
    try {
      const { data } = await api.get("/tenant/me");
      setTenant(data);
    } catch { /* silencioso — não bloqueia app */ }
  }, []);

  useEffect(() => {
    if (user) loadTenant();
    const h = () => loadTenant();
    window.addEventListener("branding-updated", h);
    return () => window.removeEventListener("branding-updated", h);
  }, [user, loadTenant]);

  useEffect(() => {
    if (authTenant) setTenant(authTenant);
  }, [authTenant]);

  if (!user) return null;
  const isAdmin = user.role === "admin";
  const isMaster = isAdmin && user.admin_level === "master";

  const displayName = tenant?.display_name || tenant?.name || "FILA Gestão";
  const logoSrc = tenant?.logo_url || "/logo.png";
  const roleLabel = isAdmin ? "Recepção" : "Profissional";
  const segmentLabel = (tenant?.segment || "").toUpperCase();
  const clientCode = tenant?.client_code || "";
  const currentYear = new Date().getFullYear();

  const adminLinks = [
    { to: t("/fila"), label: "Fila", icon: ListOrdered, testid: "nav-fila" },
    { to: t("/profissionais"), label: "Profissionais", icon: Users, testid: "nav-profissionais" },
    ...(isMaster
      ? [{ to: t("/recepcao"), label: "Recepção", icon: ShieldCheck, testid: "nav-recepcao" }]
      : []),
    { to: t("/relatorios"), label: "Relatórios", icon: History, testid: "nav-relatorios" },
    { to: t("/configuracoes"), label: "Configurações", icon: Settings, testid: "nav-config" },
  ];

  const profLinks = [
    { to: "/minha-vez", label: "Minha vez", icon: ListOrdered, testid: "nav-minha-vez" },
    { to: "/meu-status", label: "Meu status", icon: UserIcon, testid: "nav-meu-status" },
    { to: "/relatorios", label: "Relatórios", icon: History, testid: "nav-relatorios" },
  ];

  const links = isAdmin ? adminLinks : profLinks;

  const handleLogout = () => {
    logout();
    nav("/");
  };

  return (
    <div className="min-h-screen flex flex-col bg-white">
      <SupportBanner />
      <header className="sticky top-0 z-30 border-b border-gray-200 bg-white/95 backdrop-blur">
        <div className="mx-auto flex max-w-7xl items-center justify-between gap-4 px-4 py-3 md:px-8">
          <div className="flex items-center gap-3">
            <img
              src={logoSrc}
              alt={displayName}
              data-testid="header-logo"
              className="h-10 w-10 object-contain rounded-full"
              onError={(e) => {
                if (e.currentTarget.dataset.fbk !== "1") {
                  e.currentTarget.dataset.fbk = "1";
                  e.currentTarget.src = "/logo.png";
                }
              }}
            />
            <div>
              <div className="font-display text-lg font-semibold leading-none tracking-tight" data-testid="header-tenant-name">
                {displayName}
              </div>
              <div className="text-[11px] uppercase tracking-[0.2em] text-gray-500" data-testid="header-role-segment">
                {roleLabel}{segmentLabel ? ` · ${segmentLabel}` : ""}
              </div>
            </div>
          </div>
          <div className="hidden md:flex items-center gap-1">
            {links.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                data-testid={l.testid}
                className={({ isActive }) =>
                  `flex items-center gap-2 rounded-full px-4 py-2 text-sm transition-colors ${
                    isActive ? "bg-black text-white" : "text-gray-700 hover:bg-pink-50"
                  }`
                }
              >
                <l.icon size={16} />
                {l.label}
              </NavLink>
            ))}
          </div>
          <div className="flex items-center gap-3">
            <div className="hidden text-right sm:block">
              <div className="text-sm font-medium leading-none" data-testid="header-user-name">{user.name}</div>
            </div>
            {canInstall && (
              <button
                data-testid="btn-install-pwa"
                onClick={() => (canPrompt ? prompt() : setShowIosHelp(true))}
                className="hidden sm:inline-flex items-center gap-1.5 rounded-full border border-gray-200 bg-white px-3 py-1.5 text-xs font-medium text-gray-700 hover:bg-pink-50"
                title="Instalar no celular"
              >
                <Download size={14} /> Instalar
              </button>
            )}
            <button
              data-testid="btn-logout"
              onClick={handleLogout}
              className="rounded-full border border-gray-200 p-2 text-gray-700 transition-colors hover:bg-pink-50"
              aria-label="Sair"
            >
              <LogOut size={16} />
            </button>
          </div>
        </div>
        {/* mobile nav */}
        <nav className="flex overflow-x-auto border-t border-gray-100 md:hidden">
          {links.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              data-testid={`${l.testid}-mobile`}
              className={({ isActive }) =>
                `flex flex-1 min-w-max items-center justify-center gap-2 px-4 py-3 text-xs font-medium transition-colors ${
                  isActive ? "border-b-2 border-black text-black" : "text-gray-500"
                }`
              }
            >
              <l.icon size={14} />
              {l.label}
            </NavLink>
          ))}
        </nav>
      </header>

      <main className="mx-auto w-full max-w-7xl flex-1 px-4 py-6 md:px-8 md:py-10">
        {canInstall && (
          <div
            data-testid="pwa-install-banner"
            className="mb-6 flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-pink-200 bg-pink-50/70 p-4"
          >
            <div className="flex items-center gap-3">
              <div className="flex h-9 w-9 items-center justify-center rounded-full bg-black text-white">
                <Download size={16} />
              </div>
              <div>
                <div className="font-medium">Instale o app no celular</div>
                <div className="text-xs text-gray-600">
                  {isIos && !canPrompt
                    ? "No iPhone/iPad, toque em Compartilhar e depois em 'Adicionar à Tela de Início' para receber avisos com a tela travada."
                    : "Salve na tela inicial para abrir com um toque e receber avisos com a tela travada."}
                </div>
              </div>
            </div>
            <button
              data-testid="btn-install-pwa-banner"
              onClick={() => (canPrompt ? prompt() : setShowIosHelp(true))}
              className="inline-flex items-center gap-2 rounded-full bg-black px-4 py-2 text-sm font-semibold text-white hover:-translate-y-[1px] transition-transform"
            >
              {isIos && !canPrompt ? <><Share2 size={14} /> Como instalar</> : <><Download size={14} /> Instalar app</>}
            </button>
          </div>
        )}
        <Outlet />
      </main>

      {/* Rodapé institucional */}
      <footer
        data-testid="tenant-footer"
        className="border-t border-gray-100 bg-gray-50/60 px-4 py-4 md:px-8"
      >
        <div className="mx-auto flex max-w-7xl flex-col items-center gap-1 text-center text-[11px] text-gray-500 sm:flex-row sm:justify-between sm:text-left">
          <div data-testid="tenant-footer-code">
            FILA Gestão{clientCode ? ` · Cliente #${clientCode}` : ""}
          </div>
          <div>
            Desenvolvido por <span className="font-medium text-gray-700">Elite Tecnologia</span> · © {currentYear} · Todos os direitos reservados
          </div>
        </div>
      </footer>

      <Dialog open={showIosHelp} onOpenChange={setShowIosHelp}>
        <DialogContent data-testid="dialog-ios-install">
          <DialogHeader>
            <DialogTitle className="font-display">Instalar no iPhone / iPad</DialogTitle>
            <DialogDescription>
              O Safari não tem instalação automática — siga estes passos:
            </DialogDescription>
          </DialogHeader>
          <ol className="space-y-2 text-sm text-gray-700">
            <li>1. Abra o app no <strong>Safari</strong> (não use Chrome no iOS).</li>
            <li>2. Toque no botão <strong>Compartilhar</strong> (o quadradinho com a seta para cima).</li>
            <li>3. Role e toque em <strong>“Adicionar à Tela de Início”</strong>.</li>
            <li>4. Confirme o nome <em>FILA Gestão</em> e toque em <strong>Adicionar</strong>.</li>
            <li>5. Abra pelo ícone na tela inicial e permita as notificações quando o app pedir.</li>
          </ol>
        </DialogContent>
      </Dialog>
    </div>
  );
}
